import React from 'react';
import {
    Box,
    Typography,
    TextField,
    Grid,
    Button,
    IconButton,
    Paper,
    Alert,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import { OnboardingData, AdminEntry } from '../CompanyOnboardingWizard';

interface Props {
    data: OnboardingData;
    updateData: (updates: Partial<OnboardingData>) => void;
    errors: Record<string, string>;
}

const EMPTY_ADMIN: AdminEntry = {
    email: '',
    firstName: '',
    lastName: '',
    role: 'ROLE_COMPANY_ADMIN',
};

export const MultiAdminStep: React.FC<Props> = ({ data, updateData, errors }) => {
    const translate = useTranslate();

    const updateAdmin = (index: number, updates: Partial<AdminEntry>) => {
        const next = data.admins.map((a, i) => (i === index ? { ...a, ...updates } : a));
        updateData({ admins: next });
    };

    const addAdmin = () => {
        updateData({ admins: [...data.admins, { ...EMPTY_ADMIN }] });
    };

    const removeAdmin = (index: number) => {
        if (data.admins.length === 1) return; // mind. 1 Admin pflicht
        updateData({ admins: data.admins.filter((_, i) => i !== index) });
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                {translate('companyOnboarding.steps.adminsTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {translate('companyOnboarding.steps.adminsDescription')}
            </Typography>

            {errors.admins && (
                <Alert severity="error" sx={{ mb: 2 }}>{errors.admins}</Alert>
            )}

            {data.admins.map((admin, index) => (
                <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ flex: 1 }}>
                            {translate('companyOnboarding.fields.adminEntry', { index: index + 1 })}
                        </Typography>
                        {data.admins.length > 1 && (
                            <IconButton
                                size="small"
                                color="error"
                                onClick={() => removeAdmin(index)}
                                aria-label="remove admin"
                            >
                                <DeleteIcon />
                            </IconButton>
                        )}
                    </Box>

                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                label={translate('companyOnboarding.fields.adminEmail')}
                                type="email"
                                value={admin.email}
                                onChange={(e) => updateAdmin(index, { email: e.target.value })}
                                fullWidth
                                required
                                error={!!errors[`admin_${index}_email`]}
                                helperText={errors[`admin_${index}_email`]}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 3 }}>
                            <TextField
                                label={translate('companyOnboarding.fields.adminFirstName')}
                                value={admin.firstName}
                                onChange={(e) => updateAdmin(index, { firstName: e.target.value })}
                                fullWidth
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 3 }}>
                            <TextField
                                label={translate('companyOnboarding.fields.adminLastName')}
                                value={admin.lastName}
                                onChange={(e) => updateAdmin(index, { lastName: e.target.value })}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </Paper>
            ))}

            <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addAdmin}
                size="small"
            >
                {translate('companyOnboarding.buttons.addAdmin')}
            </Button>

            <Alert severity="info" sx={{ mt: 3 }}>
                {translate('companyOnboarding.warnings.adminInvitation')}
            </Alert>
        </Box>
    );
};
